import axios, { AxiosResponse } from "axios";
import qs from "qs";
import {
  ICollection,
  ICollectionAndItems,
  IUniqueCollectionAttribute,
} from "../entities/Collection/CollectionTypes";
import {
  IGetOwnerAggsLendingsResponse,
  ILending,
  LendingPageStatus,
  LendingsSortType,
} from "../entities/Lending/LendingTypes";
import {
  IFormattedNftAdditionalInfo,
  // IFormattedNftAdditionalInfo,
  IFormattedOtherContractSale,
  IFullNftOwner,
  IGetFormattedNftTransfers,
  INFT,
  INFTOwner,
  INFTStatistics,
  NFTItemsFilterType,
  NFTItemsStatusType,
} from "../entities/NFT/NFTTypes";
import { ICollectionOffer, IOfferEntry } from "../entities/Offer/OfferTypes";
import { ISaleEntry } from "../entities/Sale/SaleTypes";
import {
  getCollections,
  getCurrency,
  getSearch,
  getSortParam,
  getStatusFilter,
  getStatusParam,
} from "../hooks/methods/filterGetters";
import {
  CollectionActivityItem,
  CollectionPriceHistoryItem,
  GetCollectionAggsResponse,
  IOwnerDistribution,
  ITopOwnersItem,
} from "../pages/CollectionPage/types/CollectionPageTypes";
import { BannerSlide } from "../pages/HomePage/types/MainPageTypes";
import { SortTypes } from "../pages/MainPage/mainPageTypes";
import {
  NFT,
  ProfileFilterAggs,
  RaffleEntry,
} from "../pages/RaffflePage/types/RafffleTypes";
import { IConvertion } from "../stores/ConvertionRatesStore/ConvertionTypes";
import { FeaturesConfig } from "../stores/common/FeatureFlagsStore";
import { bannerMockupSlide, minOffsetToFetch } from "../utils/AppConstants";
import { handleError } from "../utils/handleError";

export const baseURL = `${import.meta.env.VITE_PUBLIC_API_HOST}/api/v1`;

export const getFeatureFlags = async (): Promise<FeaturesConfig | null> => {
  try {
    const response: AxiosResponse<FeaturesConfig> = await axios.get(
      `${baseURL}/settings/config`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "getFeatureFlags");
    return null;
  }
};

export const fetchCollectionsWithUserNFTs = async (
  userAddress: string
): Promise<ICollection[] | null> => {
  try {
    const response: AxiosResponse<ICollection[]> = await axios.get(
      `${baseURL}/collections/nft-owner/${userAddress}`
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchCollectionsWithUserNFTs");
    return null;
  }
};

export const fetchCollections = async (): Promise<ICollection[] | null> => {
  try {
    const response: AxiosResponse<ICollection[]> = await axios.get(
      `${baseURL}/collections`
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchCollections");
    return null;
  }
};

export const fetchCollectionsFromParams = async (
  sort: string[] | string,
  currency?: string,
  limit?: number,
  signal?: AbortSignal
): Promise<ICollection[] | null> => {
  try {
    const response: AxiosResponse<ICollection[]> = await axios.get(
      `${baseURL}/collections`,
      {
        params: {
          sort: !!sort && sort.length > 0 ? sort : null,
          currency,
          limit,
        },
        paramsSerializer: function (params) {
          return qs.stringify(params, { arrayFormat: "repeat" });
        },
        signal: signal,
      }
    );

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const fetchTelegramGiftsCollectionsFromParams = async (
  sort: string[] | string,
  currency?: string,
  limit?: number,
  signal?: AbortSignal
): Promise<ICollection[] | null> => {
  try {
    const response: AxiosResponse<ICollection[]> = await axios.get(
      `${baseURL}/collections/telegram/top`,
      {
        params: {
          sort: sort,
          currency,
          limit,
        },
        signal: signal,
      }
    );

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const fetchCollection = async (
  collectionAddress: string,
  currency?: string
): Promise<ICollection | null> => {
  try {
    const response: AxiosResponse<ICollection> = await axios.get(
      `${baseURL}/collections/${collectionAddress}`,
      {
        params: {
          currency,
        },
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchCollectionActivity = async (
  collectionAddress: string,
  sort: string,
  eventTypes: string[],
  limit: number = 100
): Promise<CollectionActivityItem[] | null> => {
  try {
    let queryString: string = "";

    eventTypes?.forEach((item) => {
      queryString += `&statuses=${item}`;
    });

    const response: AxiosResponse<{ transfers: CollectionActivityItem[] }> =
      await axios.get(
        `${baseURL}/nfts-transfer/collection/${collectionAddress}?limit=50&offset=${
          limit - 50
        }&sort=${sort}${queryString}`
      );

    return response.data.transfers;
  } catch (error) {
    throw error;
  }
};

export const fetchCollectionPriceHistoryItems = async (
  collectionAddress: string
): Promise<CollectionPriceHistoryItem[] | null> => {
  try {
    const response: AxiosResponse<{
      events: CollectionPriceHistoryItem[];
      topEvents: any;
    }> = await axios.get(
      `${baseURL}/nfts-transfer/collection/statistics/${collectionAddress}`
    );

    return response.data.events;
  } catch (error) {
    throw error;
  }
};

export const fetchOwnerDistribution = async (
  collectionAddress: string
): Promise<IOwnerDistribution | null> => {
  try {
    const response: AxiosResponse<IOwnerDistribution> = await axios.get(
      `${baseURL}/collection-owner/stats/${collectionAddress}`
    );

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const fetchTopOwnersItems = async (
  collectionAddress: string,
  limit: number
): Promise<ITopOwnersItem[] | null> => {
  try {
    const response: AxiosResponse<ITopOwnersItem[]> = await axios.get(
      `${baseURL}/collection-owner/${collectionAddress}?limit=100&offset=${
        limit - 100
      }`,
      {
        params: {
          featured: true,
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const fetchFeaturedRafffles = async (): Promise<
  RaffleEntry[] | null
> => {
  try {
    const response: AxiosResponse<RaffleEntry[]> = await axios.get(
      `${baseURL}/raffles`,
      {
        params: {
          featured: true,
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchFeaturedRafffles");
    return null;
  }
};

export const fetchRafffles = async (
  tabFilter: ReturnType<typeof getStatusParam>,
  sort: ReturnType<typeof getSortParam>,
  collections: ReturnType<typeof getCollections>,
  currency: ReturnType<typeof getCurrency>,
  search: ReturnType<typeof getSearch>
) => {
  try {
    const params = {
      ...tabFilter,
      ...sort,
      ...search,
      offset: 0,
      limit: 999,
    };

    let queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join("&");

    collections.forEach((collection) => {
      queryString += `&collectionAddress=${collection}`;
    });

    currency.forEach((item) => {
      queryString += `&ticker=${item}`;
    });
    const response: AxiosResponse<RaffleEntry[]> = await axios.get(
      `${baseURL}/raffles?${queryString}`
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchRafffles");
    return null;
  }
};

export const fetchSearchItems = async (search: string) => {
  try {
    const collections: AxiosResponse<ICollection[]> = await axios.get(
      `${baseURL}/collections`,
      {
        params: {
          search,
        },
      }
    );

    return { collections: collections.data };
  } catch (error: unknown) {
    handleError(error, "fetchSearchItems");
    return null;
  }
};

export const fetchHavePass = async (address: string) => {
  try {
    const response: AxiosResponse<{
      nft_items: NFT[];
      royaltyAddress: string;
    }> = await axios.get(
      `${baseURL}/nfts/${address}/0:7a52f81801979523b17549226b6d24fe72f486c45b4cd4141811667491310370`
    );

    return response.data.nft_items;
  } catch (error: unknown) {
    handleError(error, "fetchHavePass");
    return null;
  }
};

export const fetchNftItem = async (address: string): Promise<any | null> => {
  try {
    const response = await axios.get(`${baseURL}/raffles/${address}`);

    return response;
  } catch (error) {
    handleError(error, "fetchNftItem");
    return null;
  }
};

export const fetchBannerSlides = async (): Promise<BannerSlide[] | null> => {
  try {
    const response = await axios.get<BannerSlide[]>(`${baseURL}/promo`);

    if (!response.data.length) return bannerMockupSlide;
    return response.data;
  } catch (error) {
    return bannerMockupSlide;
  }
};

export const fetchNfts = async (
  address: string | undefined,
  collectionAddress: string
) => {
  try {
    const response = await axios.get(
      `${baseURL}/nfts/${address}/${collectionAddress}`,
      {
        params: {
          limit: 100,
          offset: 0,
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchNfts");
    return null;
  }
};

export const fetchCollectionRafflesFilterAggs = async (
  collectionAddress: string,
  ticker: string[],
  status: string
) => {
  try {
    const response: AxiosResponse<GetCollectionAggsResponse> = await axios.get(
      `${baseURL}/raffles/aggs/collection/${collectionAddress}`,
      {
        params: {
          ticker,
          status,
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchCollectionRafflesFilterAggs");
    return null;
  }
};

export const fetchProfileRaffflesFilterAggs = async (
  tabFilter: ReturnType<typeof getStatusFilter>,
  collections: ReturnType<typeof getCollections>,
  currency: ReturnType<typeof getCurrency>,
  address: string | undefined
) => {
  try {
    let queryString: string = "";

    collections.forEach((collection) => {
      queryString += `&collectionAddress=${collection}`;
    });

    currency.forEach((item) => {
      queryString += `&ticker=${item}`;
    });

    if (queryString !== "") {
      queryString = "?" + queryString.substring(1);
    }

    const response: AxiosResponse<ProfileFilterAggs | null> = await axios.get(
      `${baseURL}/raffles/aggs/${tabFilter}/${address}/${queryString}`
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchProfileRaffflesFilterAggs");
    return null;
  }
};

export const fetchRaffleEntry = async (
  address: string
): Promise<RaffleEntry | null> => {
  try {
    const response: AxiosResponse<RaffleEntry> = await axios.get(
      `${baseURL}/raffles/${address}`
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchRaffleEntry");
    return null;
  }
};

export const fetchSaleEntry = async (
  address: string
): Promise<ISaleEntry | null> => {
  if (address === "") {
    return null;
  }
  try {
    const response: AxiosResponse<ISaleEntry> = await axios.get(
      `${baseURL}/sales/${address}`
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchSaleEntry");
    return null;
  }
};

export const fetchSignNFTTransfer = async (dataObj: {
  ownerAddress: string;
  walletAddress: string;
  nftAddress: string[];
  validUntil: string;
}) => {
  try {
    const response = await axios.post(
      `${baseURL}/collection-offer/sign/forward-payload/ton`,
      dataObj
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchSignNFTTransfer");
  }
};

export const fetchBestCollectionOffer = async (
  address: string
): Promise<IOfferEntry | null> => {
  try {
    const response: AxiosResponse<IOfferEntry> = await axios.get(
      `${baseURL}/collection-offer/collection/best/${address}`
    );
    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchCollectionOffers");
    return null;
  }
};

export const fetchSignCollectionOffer = async (
  jetton: string,
  data: {
    marketplaceAddress: string;
    nftCollectionAddress: string;
    offerOwnerAddress: string;
    marketFee: string;
    royaltyFee: string;
    royaltyAddress: string;
    nftCount: number;
    validUntil: string;
    feeForTransferNft: string;
  }
): Promise<{ publicKey: string; signature: string } | null> => {
  try {
    const response = await axios.post(
      `${baseURL}/collection-offer/sign/${jetton}`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchSignCollectionOffer");
    return null;
  }
};

export const fetchSign = async (
  jetton: string,
  data: {
    admin: string;
    market: string;
    nft: string;
    endTime: string;
    tickets: string;
    ticketPrice: string;
    marketFee: string;
    maxPartOfTickets: string;
    ticker: string;
  }
): Promise<{ publicKey: string; signature: string }> => {
  try {
    const response: AxiosResponse<{
      publicKey: string;
      signature: string;
    }> = await axios.post(`${baseURL}/raffles/sign/${jetton}`, data);

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const fetchSignSale = async (
  jetton: string,
  data: {
    createdAt: string;
    marketplaceFeeAddress: string;
    marketFee: string;
    marketplaceAddress: string;
    royaltyAddress: string;
    royaltyFee: string;
    nftAddress: string;
    nftOwnerAddress: string;
    price: string;
  }
): Promise<{ publicKey: string; signature: string }> => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_PUBLIC_API_HOST}/api/v1/sales/sign/${jetton}`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const refreshRafffleData = async (
  address: string
): Promise<void | null> => {
  try {
    await axios.post(`${baseURL}/raffles/refresh`, {
      address: address,
    });
  } catch (error: any) {
    if (error?.response?.data?.message === "Internal server error") {
      return;
    }
    throw error;
  }
};

export const postListedRafffle = async (address: string) => {
  const url = `${import.meta.env.VITE_PUBLIC_API_HOST}/api/v1/raffles`;
  const data = { address: address };

  try {
    await axios.post(url, data);
  } catch (error: any) {
    if (error?.response?.data?.message === "Contract exists") {
      return;
    }
    throw error;
  }
};

export const postListedOnSaleNFT = async (address: string) => {
  const url = `${import.meta.env.VITE_PUBLIC_API_HOST}/api/v1/sales`;
  const data = { address: address };

  try {
    await axios.post(url, data);
  } catch (error: any) {
    if (error?.response?.data?.message === "Contract exists") {
      return;
    }
    throw error;
  }
};

export const refreshSaleData = async (
  address: string
): Promise<ISaleEntry | null> => {
  try {
    const response: AxiosResponse<ISaleEntry> = await axios.post(
      `${baseURL}/sales/refresh`,
      {
        address: address,
      }
    );
    return response.data;
  } catch (error: any) {
    if (error?.response?.data?.message === "Internal server error") {
      return null;
    }
    throw error;
  }
};

export const postSalesContractAddress = async (
  contractAddress: string
): Promise<boolean> => {
  try {
    return await axios.post(`${baseURL}/sales/add`, {
      address: contractAddress,
    });
  } catch (error: any) {
    if (error && error?.message.includes("Contract exists")) {
      return true;
    }
    handleError(error, "postSalesContractAddress");
    return false;
  }
};

export const postRaffflesContractAddress = async (
  contractAddress: string
): Promise<boolean> => {
  try {
    await axios.post(`${baseURL}/raffles/add`, {
      address: contractAddress,
    });
    return true;
  } catch (error: any) {
    if (error && error?.message.includes("Contract exists")) {
      return true;
    }
    // handleError(error, "postRaffflesContractAddress");
    return false;
  }
};

export const postCollectionOfferContractAddress = async (
  contractAddress: string
): Promise<void> => {
  try {
    await axios.post(`${baseURL}/collection-offer/add`, {
      address: contractAddress,
    });
  } catch (error: any) {
    if (error?.response?.data?.message === "Contract exists") {
      return;
    }
    throw error;
  }
};

export const fetchProfileCollectionOffers = async (
  walletAddress: string,
  collections: ReturnType<typeof getCollections>
) => {
  try {
    const response: AxiosResponse<any> = await axios.get(
      `${baseURL}/collection-offer/owner/${walletAddress}`,
      {
        params: {
          collectionAddress: collections,
          status: "active",
        },
      }
    );

    return response.data.offers;
  } catch (error: unknown) {
    throw error;
  }
};

export const fetchCollectionOffers = async (collectionAddress: string) => {
  try {
    const response: AxiosResponse<ICollectionOffer> = await axios.get(
      `${baseURL}/collection-offer/collection/${collectionAddress}`
    );

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const fetchProfileCollectionOffersFilterAggs = async (
  walletAddress: string
) => {
  try {
    // TODO: nik place type instead of any
    const response: AxiosResponse<any> = await axios.get(
      `${baseURL}/collection-offer/aggs/owner/${walletAddress}`
    );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchProfileCollectionOffersFilterAggs");
    return null;
  }
};

export const postCollectionOfferDeploySuccess = async (address: string) => {
  const url = `${baseURL}/collection-offer`;
  const data = { address };

  try {
    await axios.post(url, data);
  } catch (error: any) {
    if (error?.response?.data?.message === "Contract exists") {
      return;
    }
    throw error;
  }
};

export const refreshCollectionOfferData = async (masterAddress: string) => {
  const url = `${baseURL}/collection-offer/refresh`;
  const data = { address: masterAddress };

  try {
    await axios.post(url, data);
  } catch (error: any) {
    if (error?.response?.data?.message === "Internal server error") {
      return;
    }
    throw error;
  }
};

export const fetchUserCollectionOffersAmount = async (
  userAddress: string,
  collectionAddress: string
) => {
  try {
    const response: AxiosResponse<{ data: { count: number } }> =
      await axios.get(
        `${baseURL}/collection-offer/owner/count/${userAddress}`,
        {
          params: {
            collectionAddress,
          },
        }
      );

    return response.data;
  } catch (error: unknown) {
    handleError(error, "fetchUserCollectionOffersAmount");
  }
};

export const getTokensPrice = async (signal?: AbortSignal) => {
  try {
    const response: AxiosResponse<IConvertion> = await axios.get(
      `${baseURL}/price/rates`,
      {
        signal: signal,
      }
    );

    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      return null;
    }
    throw error;
  }
};

export const getUserNFTs = async (
  userAddress: string,
  limit: number = 50,
  status: NFTItemsStatusType = NFTItemsStatusType.ALL,
  sort: SortTypes = SortTypes.createdDesc,
  search: string
) => {
  const params = {
    limit: 50,
    offset: limit - 50,
    status,
    sort: search.trim().length > 0 ? null : sort,
    ...(search.trim().length > 0 ? { search } : {}),
  };
  try {
    const response: AxiosResponse<IFullNftOwner[]> = await axios.get(
      `${baseURL}/nfts/owner/${userAddress}`,
      {
        params: {
          indirectOwnership: true,
          ...params,
        },
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getNFTsByCollection = async (
  collectionAddress: string,
  limit: number = 50,
  status: NFTItemsStatusType = NFTItemsStatusType.ALL,
  sort: SortTypes = SortTypes.createdDesc,
  search: string,
  hashes?: string[] | string,
  maxPrice?: string,
  minPrice?: string,
  isOOIASales?: boolean,
  ownerAddress?: string | null,
  excludeOwnerAddress?: string | null,
  signal?: AbortSignal
): Promise<ICollectionAndItems> => {
  const params = {
    limit: 50,
    offset: limit - 50,
    status,
    sort: search.trim().length > 0 ? null : sort,
    hashes: !!hashes && hashes?.length > 0 ? hashes : null,
    ...(search.trim().length > 0 ? { search } : {}),
    ...(maxPrice ? { maxPrice } : {}),
    ...(minPrice ? { minPrice } : {}),
    isOoiaSales: isOOIASales,
    ...(ownerAddress ? { ownerAddress } : {}),
    ...(excludeOwnerAddress ? { excludeOwnerAddress } : {}),
  };

  const response: AxiosResponse<ICollectionAndItems> = await axios.get(
    `${baseURL}/nfts/collection/${collectionAddress}`,
    {
      params,
      paramsSerializer: function (params) {
        return qs.stringify(params, { arrayFormat: "repeat" });
      },
      signal,
    }
  );

  return response.data;
};

export const getNFT = async (address: string) => {
  try {
    const response: AxiosResponse<INFT> = await axios.get(
      `${baseURL}/nfts/item/${address}`
    );

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const resyncCollection = async (address: string) => {
  try {
    await axios.post(`${baseURL}/collections/resync/${address}`);
  } catch (error) {
    throw error;
  }
};

export const resyncNFTOwner = async (
  NFTAddress: string
): Promise<INFTOwner> => {
  try {
    const response: AxiosResponse<INFTOwner> = await axios.post(
      `${baseURL}/nfts/resync/owner/${NFTAddress}`
    );
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const postResyncNFTMetadata = async (NFTAddress: string) => {
  try {
    await axios.post(`${baseURL}/nfts/resync/${NFTAddress}`);
  } catch (error: unknown) {
    throw error;
  }
};

export const getCollectionAggs = async (
  collectionAddress: string,
  signal?: AbortSignal
) => {
  const response: AxiosResponse<NFTItemsFilterType> = await axios.get(
    `${baseURL}/nfts/aggs/collection/${collectionAddress}`,
    {
      signal,
    }
  );

  return response.data;
};

export const getUserNFTsAggs = async (
  ownerAddress: string,
  limit: number = 100,
  status: NFTItemsStatusType = NFTItemsStatusType.ALL
) => {
  try {
    const response: AxiosResponse<NFTItemsFilterType> = await axios.get(
      `${baseURL}/nfts/aggs/owner/${ownerAddress}`,
      {
        params: {
          indirectOwnership: true,
          limit,
          status,
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

// MARK: collections domain

// MARK: other-contracts domain

export const postRefreshOtherMarketplaceSaleData = async (
  saleAddress: string
) => {
  try {
    const response: AxiosResponse<IFormattedOtherContractSale> =
      await axios.post(
        `${baseURL}/other-contracts/sale/refresh/${saleAddress}`
      );
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

// MARK: nfts domain

export const getCollectionAttributes = async (
  collectionAddress: string,
  signal?: AbortSignal
) => {
  const response: AxiosResponse<IUniqueCollectionAttribute> = await axios.get(
    `${baseURL}/nfts/collection/attributes/${collectionAddress}`,
    {
      signal,
    }
  );

  return response.data;
};

// MARK: nfts-auction domain

export const resyncNFTOnAuction = async (auctionAddress: string) => {
  try {
    const response: AxiosResponse<{ success: boolean }> = await axios.post(
      `${baseURL}/nfts-auction/resync/${auctionAddress}`
    );
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

// MARK: nfts-transfer

export const getNFTTransfers = async (
  nftAddress: string,
  filters: string | string[],
  limit: number,
  signal?: AbortSignal
) => {
  try {
    const response: AxiosResponse<IGetFormattedNftTransfers> = await axios.get(
      `${baseURL}/nfts-transfer/${nftAddress}`,
      {
        params: {
          sort: "transfer_desc",
          statuses: filters,
          limit,
        },
        paramsSerializer: function (params) {
          return qs.stringify(params, { arrayFormat: "repeat" });
        },
        signal: signal,
      }
    );
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const getNFTStatistics = async (nftAddress: string) => {
  try {
    const response: AxiosResponse<INFTStatistics> = await axios.get(
      `${baseURL}/nfts-transfer/statistics/${nftAddress}`
    );
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

// MARK: nfts-additional domain

export const getNFTAdditionalInfo = async (nftAddress: string) => {
  try {
    const response: AxiosResponse<IFormattedNftAdditionalInfo[]> =
      await axios.get(`${baseURL}/nfts-additional/address/${nftAddress}`);

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

// MARK: lendings domain

export const fetchLendingDeploySign = async (data: {
  owner: string;
  nft: string;
  amount: string;
  loanDuration: number;
  aprAmount: string;
  platform: string;
  nftFee: string;
  platformFee: string;
}) => {
  try {
    const response: AxiosResponse<{ publicKey: string; signature: string }> =
      await axios.post(`${baseURL}/lendings/sign`, data);

    return response.data;
  } catch (error: any) {
    if (error?.response?.data?.message === "Contract exists") {
      return;
    }
    throw error;
  }
};

export const postLendingContractAddress = async (contractAddress: string) => {
  const url = `${baseURL}/lendings/add`;
  const data = { address: contractAddress };

  try {
    await axios.post(url, data);
  } catch (error: any) {
    if (error?.response?.data?.message === "Contract exists") {
      return;
    }
    throw error;
  }
};

export const postRefreshLendingData = async (address: string) => {
  const url = `${baseURL}/lendings/refresh`;
  const data = { address };
  try {
    const result: AxiosResponse<ILending> = await axios.post(url, data);

    return result.data;
  } catch (error: any) {
    return;
  }
};

export const postLendingDeploySuccess = async (address: string) => {
  const url = `${baseURL}/lendings`;
  const data = { address };
  try {
    await axios.post(url, data);
  } catch (error: any) {
    if (error?.response?.data?.message === "Contract exists") {
      return;
    }
    throw error;
  }
};

export const getLendingData = async (address: string) => {
  try {
    const response: AxiosResponse<ILending> = await axios.get(
      `${baseURL}/lendings/${address}`
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const getLendings = async (
  offset: number = minOffsetToFetch,
  status: LendingPageStatus,
  sort: LendingsSortType,
  collectionAddress: string[],
  search: string,
  ownerAddress: string | null = null,
  lenderAddress: string | null = null,
  duration: number[]
) => {
  try {
    const params = {
      limit: minOffsetToFetch,
      offset: offset - minOffsetToFetch,
      sort,
      status,
      ...(collectionAddress.length > 0 ? { collectionAddress } : {}),
      ...(ownerAddress !== null ? { ownerAddress } : {}),
      ...(lenderAddress !== null ? { lenderAddress } : {}),
      ...(search.trim().length > 0 ? { search } : {}),
      ...(duration.length > 0 ? { duration } : {}),
    };

    const response: AxiosResponse<ILending[]> = await axios.get(
      `${baseURL}/lendings`,
      {
        params,
        paramsSerializer: function (params) {
          return qs.stringify(params, { arrayFormat: "repeat" });
        },
      }
    );

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

export const getLendingsAggs = async (
  status = LendingPageStatus.auction,
  userAddress: string = "",
  collectionAddresses: string[]
) => {
  try {
    const params = {
      status,
      ...(collectionAddresses.length > 0 ? { collectionAddresses } : {}),
    };
    const url =
      userAddress !== ""
        ? `${baseURL}/lendings/aggs/${userAddress}`
        : `${baseURL}/lendings/aggs`;
    const response: AxiosResponse<IGetOwnerAggsLendingsResponse> =
      await axios.get(url, {
        params,
        paramsSerializer: function (params) {
          return qs.stringify(params, { arrayFormat: "repeat" });
        },
      });

    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

// TODO: nik remove when lending will be passed to swiftgifts
export const fetchLendingDeployCell = async (
  ownerAddress: string,
  nftAddress: string,
  amount: string,
  loanDuration: string,
  aprAmount: string
) => {
  try {
    const body = {
      owner: ownerAddress,
      nft: nftAddress,
      amount,
      loanDuration,
      aprAmount,
    };

    const response = await axios.post(
      `${baseURL}/public/lendings/create`,
      body,
      {
        headers: { Authorization: "Bearer 06d024af1" },
      }
    );

    console.log("nik response ", response.data);
    return response.data;
  } catch (error: any) {
    console.log("nik error", error);
  }
};
